import React from 'react'
import { X } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { Badge } from '@/shared/ui'
import { getTrendPointsDetail, type TrendPointsDetail, type TrendShadowRow } from '../services/trendShadowService'

const TF_LABEL: Record<string, string> = { '5': '5m', '15': '15m', '30': '30m', '60': '1h', '240': '4h', '720': '12h', D: '1D', W: '1S', M: '1M' }
const PIVOT_LABEL: Record<string, string> = {
  HH: 'Topo mais alto (HH)', HL: 'Fundo mais alto (HL)', LH: 'Topo mais baixo (LH)', LL: 'Fundo mais baixo (LL)', H: 'Topo (H)', L: 'Fundo (L)',
}
const PHASE_LABEL: Record<string, string> = {
  range: 'lateral', pivoting_up: 'virando para cima', pivoting_down: 'virando para baixo',
  seeking_higher_low: 'buscando fundo mais alto', seeking_lower_high: 'buscando topo mais baixo',
}

function trendText(v: string | null | undefined) {
  if (v === 'bullish') return 'Alta'
  if (v === 'bearish') return 'Baixa'
  if (v === 'neutral') return 'Neutra'
  return '—'
}
function TrendBadge({ value }: { value: string | null | undefined }) {
  if (value === 'bullish') return <Badge variant="soft" color="success">Alta</Badge>
  if (value === 'bearish') return <Badge variant="soft" color="error">Baixa</Badge>
  return <Badge variant="soft">{trendText(value)}</Badge>
}
function fmtPrice(v: number | null | undefined) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
  return Number(v).toLocaleString('pt-BR', { maximumSignificantDigits: 8 })
}
function fmtDate(v: string | null | undefined) {
  if (!v) return '—'
  return new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

type Point = { label: string; type?: string | null; price?: number | null; at?: string | null }
type ChartMark = { at: string; price: number; text: string; model: 'ca' | 'ts' }
type ChartLine = { price: number; text: string; model: 'ca' | 'ts' }

function StageBadge({ stage }: { stage?: string | null }) {
  if (!stage) return null
  return stage === 'final'
    ? <Badge variant="soft" color="success" className="ml-2">confirmado</Badge>
    : <Badge variant="soft" color="warning" className="ml-2">candidato</Badge>
}

function PointRow({ p, highlight, stage }: { p: Point; highlight?: boolean; stage?: string | null }) {
  return (
    <div className={`flex items-start justify-between gap-3 border-b border-border/40 py-2 text-sm ${highlight ? 'font-medium' : ''}`}>
      <div className="text-foreground/70">{p.label}{p.type ? <span className="ml-1 text-foreground/50">({p.type})</span> : null}<StageBadge stage={stage} /></div>
      <div className="text-right">
        <div>{fmtPrice(p.price)}</div>
        <div className="text-xs text-foreground/50">{fmtDate(p.at)}</div>
      </div>
    </div>
  )
}

// Gráfico simples: candles + linhas de topo/fundo e marcações de pivôs dos dois modelos
function PointsChart({ candles, marks, lines }: { candles: TrendPointsDetail['candles']; marks: ChartMark[]; lines: ChartLine[] }) {
  if (!candles.length) return <div className="py-10 text-center text-sm text-foreground/60">Sem candles para desenhar.</div>
  const W = 900, H = 300, padL = 8, padR = 96, padT = 12, padB = 12
  const lo = Math.min(...candles.map((c) => c.low), ...lines.map((l) => l.price))
  const hi = Math.max(...candles.map((c) => c.high), ...lines.map((l) => l.price))
  const span = hi - lo || hi || 1
  const y = (p: number) => padT + (1 - (p - lo) / span) * (H - padT - padB)
  const step = (W - padL - padR) / candles.length
  const x = (i: number) => padL + i * step + step / 2
  const idxByTime = new Map(candles.map((c, i) => [Date.parse(c.open_time), i]))
  const color = (m: 'ca' | 'ts') => (m === 'ca' ? '#f59e0b' : '#38bdf8')

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full rounded-md bg-background/60">
      {candles.map((c, i) => {
        const up = c.close >= c.open
        const col = up ? '#10b981' : '#ef4444'
        const top = y(Math.max(c.open, c.close))
        const bodyH = Math.max(1, Math.abs(y(c.open) - y(c.close)))
        return (
          <g key={c.open_time}>
            <line x1={x(i)} x2={x(i)} y1={y(c.high)} y2={y(c.low)} stroke={col} strokeWidth={1} opacity={0.7} />
            <rect x={x(i) - step * 0.3} y={top} width={Math.max(1, step * 0.6)} height={bodyH} fill={col} opacity={0.8} />
          </g>
        )
      })}
      {lines.map((l, k) => (
        <g key={`l${k}`}>
          <line x1={padL} x2={W - padR} y1={y(l.price)} y2={y(l.price)} stroke={color(l.model)} strokeWidth={1.2} strokeDasharray={l.model === 'ca' ? '6 4' : undefined} />
          <text x={W - padR + 4} y={y(l.price) + 4} fontSize={11} fill={color(l.model)}>{l.text}</text>
        </g>
      ))}
      {marks.map((m, k) => {
        const i = idxByTime.get(Date.parse(m.at))
        if (i === undefined) return null
        return (
          <g key={`m${k}`}>
            <circle cx={x(i)} cy={y(m.price)} r={4} fill={color(m.model)} stroke="#000" strokeWidth={0.5} />
            <text x={x(i)} y={y(m.price) + (m.model === 'ca' ? 16 : -8)} fontSize={10} textAnchor="middle" fill={color(m.model)}>{m.text}</text>
          </g>
        )
      })}
    </svg>
  )
}

export default function TrendPointsModal({ row, onClose }: { row: TrendShadowRow | null; onClose: () => void }) {
  const [detail, setDetail] = React.useState<TrendPointsDetail | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!row) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)
    getTrendPointsDetail(row.symbol, row.timeframe)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'Falha ao carregar os pontos') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [row?.symbol, row?.timeframe])

  if (!row) return null
  const s = detail?.summary ?? {}

  // Cripto Alert: último pivô de cada classificação (por fechamento) e níveis de rompimento (por pavio)
  const lastCa = (cls: string) => detail?.caPivots.find((p) => p.classification === cls) ?? null
  const caHH = lastCa('HH'), caLL = lastCa('LL'), caHL = lastCa('HL'), caLH = lastCa('LH')
  const caAbove = detail?.caLevels.find((l) => l.break_direction === 'break_above') ?? null
  const caBelow = detail?.caLevels.find((l) => l.break_direction === 'break_below') ?? null

  const marks: ChartMark[] = []
  const lines: ChartLine[] = []
  for (const p of [caHH, caLL, caHL, caLH]) if (p) marks.push({ at: p.pivot_time, price: Number(p.pivot_close), text: p.classification, model: 'ca' })
  if (caAbove) lines.push({ price: Number(caAbove.level), text: 'CA topo', model: 'ca' })
  if (caBelow) lines.push({ price: Number(caBelow.level), text: 'CA fundo', model: 'ca' })
  // Trend Structure: a tendência é a do motor; os pivôs mostrados seguem a leitura do CA (pavio extremo)
  const cp = (s.caPivots ?? null) as { trend?: string; hh?: Point; lh?: Point | null; ll?: Point; hl?: Point | null; anchor?: Point | null } | null
  const pick = (price: any, at: any): Point | null => (price != null && at ? { label: '', price: Number(price), at } : null)
  const ts = {
    HH: cp?.trend === 'bullish' ? cp.hh ?? null : pick(s.hhPrice, s.hhAt),
    LH: cp?.trend === 'bullish' ? cp.lh ?? null : pick(s.lhPrice, s.lhAt),
    LL: cp?.trend === 'bearish' ? cp.ll ?? null : pick(s.llPrice, s.llAt),
    HL: cp?.trend === 'bearish' ? cp.hl ?? null : pick(s.hlPrice, s.hlAt),
  }
  const tsTop: Point | null = cp?.trend === 'bullish' && cp.hh ? { ...cp.hh, type: 'HH' } : pick(s.validHighPrice, s.validHighAt) && { label: '', type: s.validHighType, price: s.validHighPrice, at: s.validHighAt }
  const tsBottom: Point | null = cp?.trend === 'bearish' && cp.ll ? { ...cp.ll, type: 'LL' } : pick(s.validLowPrice, s.validLowAt) && { label: '', type: s.validLowType, price: s.validLowPrice, at: s.validLowAt }
  for (const k of ['HH', 'HL', 'LH', 'LL'] as const) {
    const p = ts[k]
    if (p?.at && p.price != null) marks.push({ at: p.at, price: Number(p.price), text: k, model: 'ts' })
  }
  if (tsTop?.price != null) lines.push({ price: Number(tsTop.price), text: 'TS topo', model: 'ts' })
  if (tsBottom?.price != null) lines.push({ price: Number(tsBottom.price), text: 'TS fundo', model: 'ts' })

  return (
    <Modal open={!!row} onOpenChange={(v) => !v && onClose()} className="max-w-5xl">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-xl font-semibold">{row.symbol} · {TF_LABEL[row.timeframe] ?? row.timeframe}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-foreground/70">
            Cripto Alert <TrendBadge value={row.trend_atual} /> · Trend Structure <TrendBadge value={row.trend_v2} />
          </div>
        </div>
        <button onClick={onClose} className="rounded-md p-1 text-foreground/60 hover:bg-foreground/10" aria-label="Fechar"><X className="h-5 w-5" /></button>
      </div>

      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm">{error}</div>}
      {loading && <div className="py-10 text-center text-sm text-foreground/60">Carregando os pontos...</div>}

      {detail && (
        <>
          <PointsChart candles={detail.candles} marks={marks} lines={lines} />
          <div className="mb-5 mt-2 flex flex-wrap gap-4 text-xs text-foreground/70">
            <span><span className="mr-1 inline-block h-2 w-4 align-middle" style={{ borderTop: '2px dashed #f59e0b' }} />Cripto Alert (linhas = níveis de rompimento, pontos = pivôs por fechamento)</span>
            <span><span className="mr-1 inline-block h-2 w-4 align-middle" style={{ borderTop: '2px solid #38bdf8' }} />Trend Structure (linhas = topo e fundo válidos, pontos = HH/HL/LH/LL)</span>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-amber-500/30 p-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="font-semibold text-amber-500">Cripto Alert (atual)</div>
                <TrendBadge value={row.trend_atual} />
              </div>
              <div className="mb-3 text-xs text-foreground/60">
                Regra: <b>Alta</b> quando o último Topo mais alto (HH) é mais recente que o último Fundo mais baixo (LL); <b>Baixa</b> no caso contrário. Pivôs pelo preço de fechamento.
              </div>
              <PointRow p={{ label: 'Último HH', price: caHH?.pivot_close, at: caHH?.pivot_time }} highlight={row.trend_atual === 'bullish'} />
              <PointRow p={{ label: 'Último LL', price: caLL?.pivot_close, at: caLL?.pivot_time }} highlight={row.trend_atual === 'bearish'} />
              <PointRow p={{ label: 'Último HL', price: caHL?.pivot_close, at: caHL?.pivot_time }} />
              <PointRow p={{ label: 'Último LH', price: caLH?.pivot_close, at: caLH?.pivot_time }} />
              <div className="mt-3 text-xs font-medium text-foreground/60">Níveis de rompimento (pavio)</div>
              <PointRow p={{ label: 'Topo a romper', price: caAbove?.level, at: caAbove?.pivots?.pivot_time }} />
              <PointRow p={{ label: 'Fundo a romper', price: caBelow?.level, at: caBelow?.pivots?.pivot_time }} />
            </div>

            <div className="rounded-lg border border-sky-500/30 p-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="font-semibold text-sky-400">Trend Structure (Vela)</div>
                <TrendBadge value={s.trend ?? row.trend_v2} />
              </div>
              <div className="mb-3 text-xs text-foreground/60">
                Fase: <b>{s.phase ? PHASE_LABEL[s.phase] ?? s.phase : '—'}</b>. Estrutura válida: <b>{trendText(s.validStructureTrend)}</b>, confirmada em {fmtDate(s.validStructureConfirmedAt)}.
              </div>
              <PointRow p={{ label: 'Topo válido', type: tsTop?.type, price: tsTop?.price, at: tsTop?.at }} highlight />
              <PointRow p={{ label: 'Fundo válido', type: tsBottom?.type, price: tsBottom?.price, at: tsBottom?.at }} highlight />
              <div className="mt-1 text-xs text-foreground/50">
                {cp?.trend === 'bearish'
                  ? 'Na baixa, o LL é o pavio mais baixo desde o topo onde a baixa começou e o HL é o fundo mais baixo depois do LL. Novo LL só quando um candle fecha abaixo do pavio do LL.'
                  : 'Na alta, o HH é o pavio mais alto desde o fundo onde a alta começou e o LH é o topo mais alto depois do HH. Novo HH só quando um candle fecha acima do pavio do HH.'}
                {cp?.anchor ? ` Início da perna: ${cp.anchor.type ?? ''} ${fmtPrice(cp.anchor.price)} em ${fmtDate(cp.anchor.at)}.` : ''}
              </div>
              <PointRow p={{ label: PIVOT_LABEL.HH, price: ts.HH?.price, at: ts.HH?.at }} />
              <PointRow p={{ label: PIVOT_LABEL.HL, price: ts.HL?.price, at: ts.HL?.at }} />
              <PointRow p={{ label: PIVOT_LABEL.LH, price: ts.LH?.price, at: ts.LH?.at }} />
              <PointRow p={{ label: PIVOT_LABEL.LL, price: ts.LL?.price, at: ts.LL?.at }} />
              {s.provisionalPivotType && (
                <>
                  <div className="mt-3 text-xs font-medium text-foreground/60">Pivô em formação</div>
                  <PointRow p={{ label: PIVOT_LABEL[s.provisionalPivotType] ?? s.provisionalPivotType, price: s.provisionalPivotPrice, at: s.provisionalPivotAt }} />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}
